import {
  Box,
  Button,
  CloseButton,
  Flex,
  Text,
  SlideFade,
} from '@chakra-ui/react'
import { BellRinging, DeviceMobile } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useWebPush } from '../hooks/useWebPush'
import { sincronizarInscricao } from '../services/webPush'

/**
 * Convite para ativar o push, mostrado ao abrir o painel.
 *
 * O pedido do navegador sai do clique em "Ativar notificações" — pedir sem
 * clique é bloqueado no Firefox e no Safari e escondido pelo Chrome.
 * "Agora não" guarda a recusa por alguns dias. Quem já ativou não vê nada; a
 * inscrição só é conferida em silêncio.
 */

const CHAVE_ADIADO = 'wedrop-support:push-convite-adiado-em'
const ESPERA_DIAS = 3
const ATRASO_MS = 2500

const adiadoRecentemente = () => {
  try {
    const quando = Number(localStorage.getItem(CHAVE_ADIADO))
    return quando > 0 && Date.now() - quando < ESPERA_DIAS * 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}

const adiar = () => {
  try {
    localStorage.setItem(CHAVE_ADIADO, String(Date.now()))
  } catch {
    /* sem armazenamento: o convite volta na próxima abertura */
  }
}

export function PushPermissionPrompt() {
  const { estado, ativar, ativando } = useWebPush()
  const [visivel, setVisivel] = useState(false)

  useEffect(() => {
    if (estado === 'ativo') {
      sincronizarInscricao().catch(() => undefined)
      return
    }
    if (
      (estado === 'pendente' || estado === 'precisa-instalar') &&
      !adiadoRecentemente()
    ) {
      const timer = setTimeout(() => setVisivel(true), ATRASO_MS)
      return () => clearTimeout(timer)
    }
  }, [estado])

  if (estado !== 'pendente' && estado !== 'precisa-instalar') return null

  const fechar = () => {
    adiar()
    setVisivel(false)
  }

  const noIphone = estado === 'precisa-instalar'

  return (
    <Box
      position="fixed"
      zIndex={1300}
      bottom={{ base: 4, md: 6 }}
      right={{ base: 3, md: 6 }}
      left={{ base: 3, md: 'auto' }}
      pointerEvents={visivel ? 'auto' : 'none'}
    >
      <SlideFade in={visivel} offsetY="16px" unmountOnExit>
        <Box
          role="dialog"
          aria-labelledby="convite-push-titulo"
          w={{ base: '100%', md: '380px' }}
          bg="gray.800"
          border="1px solid"
          borderColor="gray.700"
          borderRadius="14px"
          boxShadow="0 20px 48px rgba(0, 0, 0, 0.55)"
          p={4}
        >
          <Flex gap={3} align="flex-start">
            <Flex
              boxSize="40px"
              borderRadius="12px"
              bg="rgba(236, 201, 75, 0.15)"
              color="yellow.400"
              align="center"
              justify="center"
              flexShrink={0}
            >
              {noIphone ? (
                <DeviceMobile size={22} weight="fill" />
              ) : (
                <BellRinging size={22} weight="fill" />
              )}
            </Flex>

            <Box flex="1" minW={0}>
              <Text
                id="convite-push-titulo"
                color="white"
                fontWeight={600}
                fontSize="0.95rem"
              >
                {noIphone
                  ? 'Notificações no iPhone'
                  : 'Não perca nenhum chamado'}
              </Text>
              <Text color="gray.300" fontSize="0.82rem" mt={1} lineHeight="1.5">
                {noIphone
                  ? 'Toque em Compartilhar e depois em "Adicionar à Tela de Início". Abra o painel por lá para ativar as notificações.'
                  : 'Ative as notificações deste navegador para saber de chamados novos, mensagens de clientes e tickets internos mesmo com o painel fechado.'}
              </Text>
            </Box>

            <CloseButton
              size="sm"
              color="gray.400"
              aria-label="Fechar"
              onClick={fechar}
            />
          </Flex>

          <Flex justify="flex-end" gap={2} mt={4}>
            {noIphone ? (
              <Button size="sm" variant="primary" onClick={fechar}>
                Entendi
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.300"
                  onClick={fechar}
                >
                  Agora não
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  isLoading={ativando}
                  onClick={async () => {
                    const resultado = await ativar()
                    if (resultado !== 'pendente') setVisivel(false)
                  }}
                >
                  Ativar notificações
                </Button>
              </>
            )}
          </Flex>
        </Box>
      </SlideFade>
    </Box>
  )
}

export default PushPermissionPrompt
